import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIncoming, PhoneOutgoing, ArrowLeft, Phone, Settings, Play, Pause, CheckCircle2, Send, Search, CloudUpload, Shield, X, ChevronRight, Users, Trash2, Bot, StickyNote, User, Clock3, FileText, CalendarDays, Siren } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { CallsEmptyState } from '../components/calls/CallsEmptyState';
interface CallsScreenProps {
  onNavigate: (screen: string) => void;
  onDetailView?: (isOpen: boolean) => void;
  simulatedCall?: any;
  initialCallId?: number | null;
  onInitialCallOpened?: () => void;
  onViewBookedEvent?: (eventId: number) => void;
  /** Call ids to omit from the list (used by presentation surfaces). */
  hiddenCallIds?: number[];
  /** Shows the post-call command composer inside a transcript. */
  showCommandComposer?: boolean;
  /** Shows the private command log repeated beneath a completed call summary. */
  showCommandLog?: boolean;
  /**
   * Replaces the built-in emergency demo conversation. Used by presentation
   * surfaces that need a specific emergency scenario.
   */
  emergencyScript?: {
    preamble: Message[];
    live: Message[];
    summary: string;
  };
  /**
   * Scripted live command: after the live transcript finishes, the command is
   * typed into the composer, sent to the agent, acknowledged privately, and
   * then relayed to the caller before the call wraps up.
   */
  liveCommandDemo?: {
    text: string;
    ack: string;
    spoken: Message[];
  };
}
export interface Message {
  sender: 'user' | 'ai';
  text: string;
  time: string;
  /**
   * Marks a private side-channel exchange between the account owner and their
   * AI (a command and its acknowledgement) — NOT part of the spoken call.
   * Rendered distinctly from the caller/AI conversation.
   */
  internal?: boolean;
}
interface Call {
  id: number;
  name: string;
  type: 'live' | 'incoming' | 'outgoing';
  time: string;
  duration: string;
  initials: string;
  color: string;
  group: string;
  summary: string;
  messages: Message[];
  booking?: {
    eventId: number;
    title: string;
    time: string;
  };
  isLive?: boolean;
  isFirstCall?: boolean;
  /** Flagged by the AI when the caller reports an urgent situation. */
  isEmergency?: boolean;
}
export function CallsScreen({
  onNavigate,
  onDetailView,
  simulatedCall,
  initialCallId,
  onInitialCallOpened,
  onViewBookedEvent,
  hiddenCallIds,
  showCommandComposer = true,
  showCommandLog = true,
  emergencyScript,
  liveCommandDemo
}: CallsScreenProps) {
  const {
    isLight
  } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [selectedCaller, setSelectedCaller] = useState<string | null>(null);
  const [callerOverviewCall, setCallerOverviewCall] = useState<Call | null>(null);
  const [playingIdx, setPlayingIdx] = useState<number | null>(null);
  const [commandInput, setCommandInput] = useState('');
  const detailScrollRef = useRef<HTMLDivElement>(null);
  const [liveTyping, setLiveTyping] = useState(false);
  const liveSimTimerRef = useRef<NodeJS.Timeout | null>(null);
  const liveSimActiveRef = useRef(false);
  // Backup prompt state
  const [backupPromptShown, setBackupPromptShown] = useState(false);
  const [showBackupPrompt, setShowBackupPrompt] = useState(false);
  const [backupStep, setBackupStep] = useState<'ask' | 'provider' | 'frequency' | 'done'>('ask');
  const [backupFrequency, setBackupFrequency] = useState<string | null>(null);
  const [backupProvider, setBackupProvider] = useState<string | null>(null);
  // Call confirmation state
  const [showCallConfirm, setShowCallConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletedCallIds, setDeletedCallIds] = useState<number[]>([]);
  // A brand-new account has no call history until Vexu answers its first call.
  const [callsSeeded, setCallsSeeded] = useState(false);
  // Once a simulated live call wraps up it becomes a normal log entry.
  const [completedLiveCall, setCompletedLiveCall] = useState<Call | null>(null);
  // What was already said before you opened the emergency call — the transcript
  // opens mid-conversation so you can catch up instantly.
  const emergencyCallPreamble: Message[] = [{
    sender: 'ai',
    text: "Hello, you've reached Fadi's assistant. How can I help you?",
    time: '2:30 PM'
  }, {
    sender: 'user',
    text: "This is urgent — there's a water leak flooding the server room at the Harbour office. I need Fadi now.",
    time: '2:30 PM'
  }, {
    sender: 'ai',
    text: 'Understood. I’m treating this as an emergency. Is anyone in immediate danger?',
    time: '2:31 PM'
  }, {
    sender: 'user',
    text: 'No one is hurt, but the racks are still powered and water is rising.',
    time: '2:31 PM'
  }];
  // The part that streams in live while you watch.
  const emergencyCallScript: Message[] = [{
    sender: 'ai',
    text: 'Hold on, please stay on the line — I’m trying to reach Fadi right now.',
    time: '2:31 PM'
  }, {
    sender: 'ai',
    text: 'I’ve just sent Fadi a notification flagged as an urgent emergency.',
    time: '2:32 PM'
  }, {
    sender: 'user',
    text: 'Please hurry, the water is getting close to the power units.',
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: 'I’m still trying to reach Fadi — staying on it. Please move away from the racks meanwhile.',
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: 'Fadi just replied — he’ll call you back in 5 minutes. Please keep this line free.',
    time: '2:33 PM'
  }];
  // Full conversation script for the live call simulation
  const liveCallScript: Message[] = [{
    sender: 'ai',
    text: "Hello, you've reached the assistant. How can I help you?",
    time: '2:30 PM'
  }, {
    sender: 'user',
    text: 'Hi, is Sarah available? I need to discuss the project deadline.',
    time: '2:30 PM'
  }, {
    sender: 'ai',
    text: "She's currently unavailable. I can take a message or schedule a callback.",
    time: '2:31 PM'
  }, {
    sender: 'user',
    text: "Please tell her to call me back, it's about the Q4 report.",
    time: '2:31 PM'
  }, {
    sender: 'ai',
    text: "Of course. I'll mark this as high priority. Can I get your name?",
    time: '2:31 PM'
  }, {
    sender: 'user',
    text: "It's James from the finance team.",
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: "Got it, James. I'll let Sarah know right away. Is there anything else?",
    time: '2:32 PM'
  }, {
    sender: 'user',
    text: "No, that's all. Thanks!",
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: "You're welcome. Have a great day!",
    time: '2:32 PM'
  }];
  // Notify parent when a caller overview or transcript opens/closes.
  useEffect(() => {
    onDetailView?.(selectedCall !== null || selectedCaller !== null);
  }, [selectedCall, selectedCaller]);
  // Live call simulation: deliver messages one by one using recursive setTimeout
  useEffect(() => {
    if (!selectedCall?.isLive) {
      // Clean up if we leave the live call
      liveSimActiveRef.current = false;
      if (liveSimTimerRef.current) {
        clearTimeout(liveSimTimerRef.current);
        liveSimTimerRef.current = null;
      }
      return;
    }
    // Prevent double-start (React StrictMode)
    if (liveSimActiveRef.current) return;
    liveSimActiveRef.current = true;
    const isEmergencyCall = Boolean(selectedCall.isEmergency);
    const activePreamble = emergencyScript?.preamble ?? emergencyCallPreamble;
    const activeScript = isEmergencyCall ? emergencyScript?.live ?? emergencyCallScript : liveCallScript;
    // An emergency opens mid-call: show what was already said, then stream on.
    setSelectedCall(prev => prev ? {
      ...prev,
      messages: isEmergencyCall ? activePreamble : []
    } : prev);
    setLiveTyping(false);
    setCommandInput('');

    // The live call plays as a list of steps: spoken messages, plus (optionally)
    // one scripted command the owner types mid-call.
    type LiveStep = {
      kind: 'msg';
      msg: Message;
    } | {
      kind: 'command';
    };
    const steps: LiveStep[] = activeScript.map(msg => ({
      kind: 'msg' as const,
      msg
    }));
    if (liveCommandDemo) {
      steps.push({
        kind: 'command'
      });
      liveCommandDemo.spoken.forEach(msg => steps.push({
        kind: 'msg' as const,
        msg
      }));
    }
    const appendMessage = (msg: Message) => {
      setSelectedCall(prev => prev ? {
        ...prev,
        messages: [...prev.messages, msg]
      } : prev);
      setTimeout(() => {
        detailScrollRef.current?.scrollTo({
          top: detailScrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    };
    const stamp = () => new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });

    /** Types the command into the composer, sends it, then the agent acks. */
    const runCommandStep = (done: () => void) => {
      if (!liveCommandDemo) return done();
      const {
        text,
        ack
      } = liveCommandDemo;
      let charIndex = 0;
      const typeNext = () => {
        if (!liveSimActiveRef.current) return;
        charIndex += 1;
        setCommandInput(text.slice(0, charIndex));
        if (charIndex < text.length) {
          liveSimTimerRef.current = setTimeout(typeNext, 38);
          return;
        }
        // Send it.
        liveSimTimerRef.current = setTimeout(() => {
          if (!liveSimActiveRef.current) return;
          setCommandInput('');
          appendMessage({
            sender: 'user',
            text,
            internal: true,
            time: stamp()
          });
          liveSimTimerRef.current = setTimeout(() => {
            if (!liveSimActiveRef.current) return;
            appendMessage({
              sender: 'ai',
              text: ack,
              internal: true,
              time: stamp()
            });
            liveSimTimerRef.current = setTimeout(done, 900);
          }, 1100);
        }, 600);
      };
      liveSimTimerRef.current = setTimeout(typeNext, 900);
    };
    let currentIndex = 0;
    const deliverNextMessage = () => {
      if (!liveSimActiveRef.current) return;
      if (currentIndex >= steps.length) {
        // All messages delivered — end the call
        setLiveTyping(false);
        liveSimTimerRef.current = setTimeout(() => {
          if (!liveSimActiveRef.current) return;
          setSelectedCall(prev => {
            if (!prev) return prev;
            const endedCall: Call = {
              ...prev,
              type: 'incoming',
              isLive: false,
              duration: isEmergencyCall ? '3m 08s' : '2m 32s',
              time: isEmergencyCall ? '2:33 PM' : '2:32 PM',
              summary: isEmergencyCall ? emergencyScript?.summary ?? 'Emergency reported by Michael Torres (facilities): water leak flooding the Harbour office server room. No injuries. Vexu kept the caller on the line, sent Fadi an urgent emergency alert, and confirmed Fadi will call back within 5 minutes.' : 'James from finance called regarding the Q4 report deadline. Requested a callback from Sarah. Marked as high priority.'
            };
            // Keep the finished call in the log instead of a stale "live" row.
            setCompletedLiveCall(endedCall);
            return endedCall;
          });
          liveSimActiveRef.current = false;
          // Scroll to summary
          setTimeout(() => {
            detailScrollRef.current?.scrollTo({
              top: detailScrollRef.current.scrollHeight,
              behavior: 'smooth'
            });
          }, 500);
        }, 1500);
        return;
      }
      const step = steps[currentIndex];
      if (step.kind === 'command') {
        runCommandStep(() => {
          currentIndex++;
          deliverNextMessage();
        });
        return;
      }
      const typingDelay = currentIndex === 0 ? 800 : 1200;
      const messageDelay = step.msg.sender === 'ai' ? 1800 : 1400;
      // Show typing indicator
      liveSimTimerRef.current = setTimeout(() => {
        if (!liveSimActiveRef.current) return;
        setLiveTyping(true);
        // Deliver the message
        liveSimTimerRef.current = setTimeout(() => {
          if (!liveSimActiveRef.current) return;
          setLiveTyping(false);
          appendMessage(step.msg);
          currentIndex++;
          // Schedule next message
          deliverNextMessage();
        }, messageDelay);
      }, typingDelay);
    };
    // Start the simulation after a brief initial delay
    liveSimTimerRef.current = setTimeout(() => {
      deliverNextMessage();
    }, 600);
    return () => {
      liveSimActiveRef.current = false;
      if (liveSimTimerRef.current) {
        clearTimeout(liveSimTimerRef.current);
        liveSimTimerRef.current = null;
      }
    };
  }, [selectedCall?.isLive, selectedCall?.id]);
  // Keep the typing indicator in view — it renders below the last message and
  // would otherwise sit behind the floating command composer.
  useEffect(() => {
    if (!liveTyping) return;
    const id = setTimeout(() => {
      detailScrollRef.current?.scrollTo({
        top: detailScrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }, 60);
    return () => clearTimeout(id);
  }, [liveTyping]);
  // Auto-scroll for non-live calls
  useEffect(() => {
    if (selectedCall && !selectedCall.isLive && detailScrollRef.current) {
      setTimeout(() => {
        detailScrollRef.current?.scrollTo({
          top: detailScrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 400);
    }
  }, [selectedCall]);
  const baseCalls: Call[] = [{
    id: 1,
    name: 'Sarah Miller',
    type: 'live',
    time: 'Now',
    duration: '1m 42s',
    initials: 'SM',
    color: isLight ? 'bg-purple-100 text-purple-700' : 'bg-purple-100/60 text-purple-600',
    group: 'Work Contacts',
    isLive: true,
    summary: '',
    messages: []
  }, {
    id: 12,
    name: 'Nadia Haddad',
    type: 'incoming',
    time: '12:04 PM',
    duration: '1m 58s',
    initials: 'NH',
    color: isLight ? 'bg-red-100 text-red-700' : 'bg-red-500/20 text-red-400',
    group: 'Emergency',
    isEmergency: true,
    summary: 'Nadia Haddad reported a gas smell in the ground-floor unit at Marina Heights. No one injured, building being evacuated. Vexu flagged the call as an emergency and escalated it to you for an immediate callback.',
    messages: [{
      sender: 'ai',
      text: "Hello, you've reached the assistant. How can I help you?",
      time: '12:04 PM'
    }, {
      sender: 'user',
      text: 'There is a strong gas smell in the ground-floor unit at Marina Heights. We need someone now.',
      time: '12:04 PM'
    }, {
      sender: 'ai',
      text: 'I’m treating this as an emergency. Is anyone injured or still inside the unit?',
      time: '12:05 PM'
    }, {
      sender: 'user',
      text: 'No injuries. We are evacuating the floor right now.',
      time: '12:05 PM'
    }, {
      sender: 'ai',
      text: 'Understood. Keep everyone outside and away from the entrance. I’m alerting the team and sharing your number for an immediate callback.',
      time: '12:06 PM'
    }]
  }, {
    id: 2,
    name: 'Mike Ross',
    type: 'incoming',
    time: '11:15 AM',
    duration: '2m 14s',
    initials: 'MR',
    color: isLight ? 'bg-blue-100 text-blue-700' : 'bg-blue-100/60 text-blue-600',
    group: 'VIP Clients',
    summary: 'Caller scheduled a meeting for tomorrow at 2 PM. Calendar booking confirmed.',
    booking: {
      eventId: 2,
      title: 'Client Discovery Call',
      time: '2:00 PM – 2:45 PM'
    },
    messages: [{
      sender: 'ai',
      text: 'Thank you for calling. How may I assist you?',
      time: '11:15 AM'
    }, {
      sender: 'user',
      text: "I'd like to schedule a meeting for tomorrow afternoon.",
      time: '11:15 AM'
    }, {
      sender: 'ai',
      text: 'I can check the calendar. What time works best for you?',
      time: '11:16 AM'
    }, {
      sender: 'user',
      text: 'Around 2 PM if possible.',
      time: '11:16 AM'
    }, {
      sender: 'ai',
      text: "Let me check... 2 PM is available. I'll book that for you.",
      time: '11:16 AM'
    }, {
      sender: 'user',
      text: 'Perfect, thanks!',
      time: '11:17 AM'
    }]
  }, {
    id: 3,
    name: '+1 (555) 012-3456',
    type: 'outgoing',
    time: 'Yesterday',
    duration: '45s',
    initials: '#',
    color: isLight ? 'bg-slate-200 text-slate-700' : 'bg-slate-100/60 text-slate-600',
    group: 'All Contacts',
    summary: 'Brief call — wrong number. No action required.',
    messages: [{
      sender: 'ai',
      text: "Hello, you've reached the assistant.",
      time: '4:12 PM'
    }, {
      sender: 'user',
      text: 'Wrong number, sorry.',
      time: '4:12 PM'
    }, {
      sender: 'ai',
      text: 'No problem at all. Have a great day!',
      time: '4:12 PM'
    }]
  }, {
    id: 4,
    name: 'Design Team',
    type: 'incoming',
    time: 'Mon',
    duration: '5m 30s',
    initials: 'DT',
    color: isLight ? 'bg-vexu/20 text-vexu-700' : 'bg-emerald-100/60 text-emerald-600',
    group: 'Work Contacts',
    summary: 'Urgent request for design mockup approval before EOD. Priority notification sent. Client presentation scheduled for tomorrow.',
    messages: [{
      sender: 'ai',
      text: 'Thank you for calling. They are currently unavailable.',
      time: '3:45 PM'
    }, {
      sender: 'user',
      text: 'This is urgent — we need approval on the design mockups before EOD.',
      time: '3:45 PM'
    }, {
      sender: 'ai',
      text: 'I understand this is urgent. Let me try to reach them right away.',
      time: '3:46 PM'
    }, {
      sender: 'user',
      text: 'Please do. The client presentation is tomorrow morning.',
      time: '3:46 PM'
    }, {
      sender: 'ai',
      text: "I've sent a priority notification. They should get back to you shortly.",
      time: '3:47 PM'
    }, {
      sender: 'user',
      text: "Great, I'll wait for their call.",
      time: '3:47 PM'
    }]
  }, {
    id: 5,
    name: 'Mom',
    type: 'incoming',
    time: 'Sun',
    duration: '1m 12s',
    initials: 'M',
    color: isLight ? 'bg-pink-100 text-pink-700' : 'bg-pink-100/60 text-pink-600',
    group: 'Family',
    isFirstCall: true,
    summary: 'First call from Mom. Requested a callback when available. No urgency.',
    messages: [{
      sender: 'ai',
      text: "Hi! They're not available right now but I can take a message.",
      time: '6:20 PM'
    }, {
      sender: 'user',
      text: "Just tell them to call me when they're free. Nothing urgent.",
      time: '6:20 PM'
    }, {
      sender: 'ai',
      text: "Got it! I'll let them know. Anything else?",
      time: '6:21 PM'
    }]
  }, {
    id: 6,
    name: 'Sarah Miller',
    type: 'incoming',
    time: 'Yesterday · 4:20 PM',
    duration: '3m 08s',
    initials: 'SM',
    color: isLight ? 'bg-purple-100 text-purple-700' : 'bg-purple-100/60 text-purple-600',
    group: 'Work Contacts',
    summary: 'Sarah checked in on the product launch timeline and asked for a callback after the client review.',
    messages: [{
      sender: 'ai',
      text: 'Hi, you have reached the assistant. How can I help?',
      time: '4:20 PM'
    }, {
      sender: 'user',
      text: 'Could you ask them to call me after the client review? I want to check the launch timeline.',
      time: '4:21 PM'
    }, {
      sender: 'ai',
      text: 'Absolutely. I will pass that along.',
      time: '4:22 PM'
    }]
  }, {
    id: 7,
    name: 'Sarah Miller',
    type: 'incoming',
    time: 'Mon · 10:05 AM',
    duration: '1m 46s',
    initials: 'SM',
    color: isLight ? 'bg-purple-100 text-purple-700' : 'bg-purple-100/60 text-purple-600',
    group: 'Work Contacts',
    summary: 'Sarah shared revised presentation notes and requested confirmation before tomorrow morning.',
    messages: [{
      sender: 'ai',
      text: 'Thanks for calling. How may I assist?',
      time: '10:05 AM'
    }, {
      sender: 'user',
      text: 'I have revised presentation notes. Please ask them to confirm they received them.',
      time: '10:06 AM'
    }, {
      sender: 'ai',
      text: 'I will make sure they receive that message.',
      time: '10:06 AM'
    }]
  }, {
    id: 8,
    name: 'Sarah Miller',
    type: 'incoming',
    time: 'Fri · 2:40 PM',
    duration: '2m 21s',
    initials: 'SM',
    color: isLight ? 'bg-purple-100 text-purple-700' : 'bg-purple-100/60 text-purple-600',
    group: 'Work Contacts',
    summary: 'Sarah asked about availability for a planning session next week. No booking was made.',
    messages: [{
      sender: 'ai',
      text: 'Hello, how can I help today?',
      time: '2:40 PM'
    }, {
      sender: 'user',
      text: 'I am trying to find a time for a planning session next week.',
      time: '2:41 PM'
    }, {
      sender: 'ai',
      text: 'I will let them know and ask them to follow up with availability.',
      time: '2:42 PM'
    }]
  }, {
    id: 9,
    name: 'Mike Ross',
    type: 'incoming',
    time: 'Yesterday · 9:30 AM',
    duration: '1m 38s',
    initials: 'MR',
    color: isLight ? 'bg-blue-100 text-blue-700' : 'bg-blue-100/60 text-blue-600',
    group: 'VIP Clients',
    summary: 'Mike asked for the latest proposal version before sharing it with his team.',
    messages: [{
      sender: 'ai',
      text: 'Hello, how can I help today?',
      time: '9:30 AM'
    }, {
      sender: 'user',
      text: 'Could you ask them to send over the latest proposal?',
      time: '9:31 AM'
    }, {
      sender: 'ai',
      text: 'I will pass that request along.',
      time: '9:31 AM'
    }]
  }, {
    id: 10,
    name: 'Mike Ross',
    type: 'incoming',
    time: 'Tue · 3:15 PM',
    duration: '2m 05s',
    initials: 'MR',
    color: isLight ? 'bg-blue-100 text-blue-700' : 'bg-blue-100/60 text-blue-600',
    group: 'VIP Clients',
    summary: 'Mike requested a callback to review contract feedback before the end of the week.',
    messages: [{
      sender: 'ai',
      text: 'Thanks for calling. What can I help with?',
      time: '3:15 PM'
    }, {
      sender: 'user',
      text: 'I have contract feedback to review this week.',
      time: '3:16 PM'
    }, {
      sender: 'ai',
      text: 'I will make sure they receive your message.',
      time: '3:17 PM'
    }]
  }, {
    id: 11,
    name: 'Design Team',
    type: 'incoming',
    time: 'Yesterday · 1:10 PM',
    duration: '2m 48s',
    initials: 'DT',
    color: isLight ? 'bg-vexu/20 text-vexu-700' : 'bg-emerald-100/60 text-emerald-600',
    group: 'Work Contacts',
    summary: 'The team requested feedback on the onboarding illustrations and noted a Thursday delivery target.',
    messages: [{
      sender: 'ai',
      text: 'Hello, how can I help?',
      time: '1:10 PM'
    }, {
      sender: 'user',
      text: 'We need feedback on the onboarding illustrations by Thursday.',
      time: '1:11 PM'
    }, {
      sender: 'ai',
      text: 'I will share that priority with them.',
      time: '1:12 PM'
    }]
  }];
  const historyCalls: Call[] = callsSeeded ? baseCalls : [];
  const calls: Call[] = simulatedCall ? [completedLiveCall ?? {
    id: 999,
    name: simulatedCall.name,
    type: 'live',
    time: 'Now',
    duration: '0:00',
    initials: simulatedCall.initials,
    color: isLight ? simulatedCall.colorLight : simulatedCall.colorDark,
    group: simulatedCall.group,
    isLive: true,
    isEmergency: Boolean(simulatedCall.isEmergency),
    summary: '',
    messages: []
  }, ...historyCalls] : historyCalls;
  const visibleCalls = calls.filter(c => !deletedCallIds.includes(c.id) && !hiddenCallIds?.includes(c.id));
  const showEmptyState = !callsSeeded && !simulatedCall;
  // A live call or a deep link from elsewhere implies real history exists.
  useEffect(() => {
    if (simulatedCall && !callsSeeded) setCallsSeeded(true);
  }, [simulatedCall, callsSeeded]);
  // A fresh simulated call replaces any previously completed one.
  useEffect(() => {
    setCompletedLiveCall(null);
  }, [simulatedCall]);
  useEffect(() => {
    if (initialCallId === null || initialCallId === undefined) return;
    if (!callsSeeded) {
      setCallsSeeded(true);
      return;
    }
    const referencedCall = calls.find(call => call.id === initialCallId);
    if (referencedCall) {
      setSelectedCaller(null);
      setSelectedCall(referencedCall);
      setPlayingIdx(null);
      setCommandInput('');
      setLiveTyping(false);
    }
    onInitialCallOpened?.();
  }, [initialCallId, calls, onInitialCallOpened]);
  const filteredCalls = searchQuery.trim() ? visibleCalls.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.group.toLowerCase().includes(searchQuery.toLowerCase())) : visibleCalls;
  const callerHistoryCalls = selectedCaller ? visibleCalls.filter(call => call.name === selectedCaller && !call.isLive && call.id !== callerOverviewCall?.id) : [];
  const callerProfile = callerOverviewCall ?? callerHistoryCalls[0];
  const getCallPhoneNumber = (call: Call) => {
    const numbers: Record<number, string> = {
      1: '+1 (415) 555-0182',
      2: '+1 (646) 555-0134',
      4: '+1 (212) 555-0168',
      5: '+1 (415) 555-0101',
      6: '+1 (415) 555-0182',
      7: '+1 (628) 555-0147',
      8: '+1 (415) 555-0182',
      9: '+1 (646) 555-0134',
      10: '+1 (917) 555-0196',
      11: '+1 (212) 555-0168'
    };
    return numbers[call.id] ?? '+1 (555) 012-3456';
  };
  const selectedCallHistoryCount = selectedCall ? visibleCalls.filter(call => call.name === selectedCall.name && !call.isLive && call.id !== selectedCall.id).length : 0;
  const handleDeleteCall = () => {
    if (!selectedCall) return;
    const remainingCallerCalls = visibleCalls.filter(call => call.name === selectedCall.name && call.id !== selectedCall.id && !call.isLive);
    setDeletedCallIds(prev => [...prev, selectedCall.id]);
    setShowDeleteConfirm(false);
    setSelectedCall(null);
    if (selectedCaller && remainingCallerCalls.length === 0) {
      setSelectedCaller(null);
    }
  };
  const togglePlay = (idx: number) => {
    if (playingIdx === idx) {
      setPlayingIdx(null);
    } else {
      setPlayingIdx(idx);
      setTimeout(() => setPlayingIdx(null), 2000);
    }
  };
  // Renders a private owner↔Vexu command message using the SAME bubble
  // structure as the call conversation (avatar + name label + aligned bubble +
  // time), but in a distinct amber "private" color so it reads as internal.
  // No play button — commands are typed, not spoken/recorded.
  const renderInternalBubble = (m: Message, key: React.Key) => {
    const isCommand = m.sender === 'user';
    return <motion.div key={key} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.2
    }} className={`flex items-end gap-2 pt-1 ${isCommand ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-1755" data-mp="mhrpeu47tx17">
        {/* Avatar */}
        <div className="w-7 flex-shrink-0 self-end" data-id="element-1756" data-mp="15a3rrguvcybc">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isLight ? 'bg-amber-100 text-amber-700' : 'bg-amber-400/15 text-amber-300'}`} data-id="element-1757" data-mp="15alsoqsh80pl">
            {isCommand ? <User className="w-3.5 h-3.5" data-id="element-1758" data-mp="ve0og7oa6tmt" /> : <Bot className="w-4 h-4" data-id="element-1759" data-mp="3l1rj8wr45ymr" />}
          </div>
        </div>

        <div className={`flex flex-col max-w-[68%] ${isCommand ? 'items-end' : 'items-start'}`} data-id="element-1760" data-mp="3g1lx1w3m54c2">
          {/* Speaker name label */}
          <span className={`flex items-center gap-1 text-[10px] font-semibold mb-1 px-0.5 ${isLight ? 'text-amber-700/80' : 'text-amber-300/80'}`} data-id="element-1761" data-mp="2e7we5u67jlme">
            {isCommand ? 'You' : 'Vexu'}
          </span>
          <div className={`rounded-[14px] px-3 py-2 ${isCommand ? isLight ? 'bg-amber-100 text-amber-900 rounded-tr-[4px] border border-amber-200/80' : 'bg-amber-400/[0.16] text-amber-50 rounded-tr-[4px] border border-amber-400/20' : isLight ? 'bg-amber-50 text-amber-900/90 rounded-tl-[4px] border border-amber-200/60' : 'bg-amber-400/[0.08] text-amber-100/90 rounded-tl-[4px] border border-amber-400/[0.12]'}`} data-id="element-1762" data-mp="2j8sgw4etp1q8">
            <p className="text-[12px] leading-[1.45] tracking-[-0.01em]" data-id="element-1763" data-mp="320cx06xrrhmr">
              {m.text}
            </p>
            <div className={`flex items-center justify-end gap-1 mt-0.5 ${isLight ? 'text-amber-600/60' : 'text-amber-300/50'}`} data-id="element-1764" data-mp="1cl5ctht8srof">
              <span className="text-[9px] tracking-wide" data-id="element-1765" data-mp="3j8w35tc2dn69">{m.time}</span>
            </div>
          </div>
        </div>
      </motion.div>;
  };
  const openCallDetail = (call: Call) => {
    liveSimActiveRef.current = false;
    if (liveSimTimerRef.current) {
      clearTimeout(liveSimTimerRef.current);
      liveSimTimerRef.current = null;
    }
    setSelectedCall(call);
    setPlayingIdx(null);
    setCommandInput('');
    setLiveTyping(false);
  };
  const handleSelectCall = (call: Call) => {
    // A list row always opens the exact conversation the user tapped.
    // Repeat-caller history remains available inside that transcript.
    setSelectedCaller(null);
    openCallDetail(call);
  };
  const handleBack = () => {
    liveSimActiveRef.current = false;
    if (liveSimTimerRef.current) {
      clearTimeout(liveSimTimerRef.current);
      liveSimTimerRef.current = null;
    }
    setLiveTyping(false);
    // Return from a transcript to its caller history before leaving Calls.
    if (selectedCaller) {
      setSelectedCall(null);
      return;
    }
    // Show backup prompt on first back press
    if (!backupPromptShown) {
      setShowBackupPrompt(true);
      setBackupStep('ask');
      return;
    }
    setSelectedCall(null);
  };
  const handleCallerBack = () => {
    // Caller history was opened from a transcript: return to that exact call.
    if (callerOverviewCall) {
      openCallDetail(callerOverviewCall);
      setSelectedCaller(null);
      setCallerOverviewCall(null);
      return;
    }
    setSelectedCaller(null);
    setPlayingIdx(null);
  };
  const handleBackupEnable = () => {
    setBackupStep('provider');
  };
  const handleBackupProviderSelect = (provider: string) => {
    setBackupProvider(provider);
    setBackupStep('frequency');
  };
  const handleBackupFrequencySelect = (freq: string) => {
    setBackupFrequency(freq);
    setBackupStep('done');
    setTimeout(() => {
      setShowBackupPrompt(false);
      setBackupPromptShown(true);
      setSelectedCall(null);
    }, 1500);
  };
  const handleBackupSkip = () => {
    setShowBackupPrompt(false);
    setBackupPromptShown(true);
    setSelectedCall(null);
  };
  const handleSendCommand = () => {
    if (!commandInput.trim() || !selectedCall) return;
    // Add the command as a private, internal owner→AI message.
    const newMsg: Message = {
      sender: 'user',
      text: commandInput.trim(),
      internal: true,
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
    setSelectedCall({
      ...selectedCall,
      messages: [...selectedCall.messages, newMsg]
    });
    setCommandInput('');
    // Simulate AI response after 1s (also internal — the caller never hears it)
    setTimeout(() => {
      setSelectedCall(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: [...prev.messages, {
            sender: 'ai',
            text: "Got it, I'll take care of that right away.",
            internal: true,
            time: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })
          }]
        };
      });
    }, 1000);
  };
  return <div className="h-full flex flex-col relative overflow-hidden" data-id="element-1766" data-mp="ec00xt3jdbi7">
      {/* Call List View */}
      <AnimatePresence data-id="element-1767" data-mp="9dgd1n5dtpn7">
        {!selectedCall && !selectedCaller && <motion.div key="list" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -40
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0 flex flex-col" data-id="element-1768" data-mp="3hrfhzeac0lcu">
            {/* Header */}
            <div className={`px-5 pt-6 pb-3 relative z-10 ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-1769" data-mp="120j8u7iobm9r">
              <div className="flex items-center justify-between mb-3" data-id="element-1770" data-mp="19hpk0tfvilj0">
                <h1 className={`text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1771" data-mp="1ein9lr37qhuj">
                  Calls
                </h1>
                <button data-tour="calls-caller-experience" data-sim="caller-experience" onClick={() => onNavigate('callerExperience')} className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20 hover:bg-vexu/20' : 'bg-vexu/10 text-vexu border border-vexu/20 hover:bg-vexu/20'}`} data-id="element-1772" data-mp="14jxprsrranbn">
                  <Users className="w-3.5 h-3.5" data-id="element-1773" data-mp="1ngp6pvjtsqcj" />
                  Caller Experience
                </button>
              </div>
              {/* Search Bar */}
              <div data-tour="calls-search" className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl transition-colors ${isLight ? 'bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]' : 'bg-[#141414] border border-white/[0.06]'}`} data-id="element-1774" data-mp="2g04jwbt7nkz5">
                <Search className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1775" data-mp="2gt3lsxmx3a2q" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search calls..." className={`flex-1 text-[13px] bg-transparent outline-none ${isLight ? 'text-slate-900 placeholder:text-slate-400' : 'text-white placeholder:text-zinc-500'}`} data-id="element-1776" data-mp="26w79egtwe3sw" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className={`text-[10px] font-medium px-2 py-0.5 rounded-md ${isLight ? 'text-slate-400 hover:text-slate-600 bg-slate-100' : 'text-zinc-500 hover:text-zinc-300 bg-white/5'}`} data-id="element-1777" data-mp="1o4y28e7lnr4h">
                    Clear
                  </button>}
              </div>
            </div>

            {/* Full-width Call List */}
            <div className="flex-1 overflow-y-auto pb-36 relative z-10" data-id="element-1778" data-mp="8l750m0p7cv5">
              {showEmptyState ? <CallsEmptyState onLoadSampleCalls={() => setCallsSeeded(true)} onOpenCallerExperience={() => onNavigate('callerExperience')} data-id="element-1779" data-mp="3c3qoaz1ef8dt" /> : filteredCalls.length === 0 ? <div className="flex flex-col items-center justify-center py-16" data-id="element-1780" data-mp="3l3dnde6no23p">
                  <Search className={`w-8 h-8 mb-3 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1781" data-mp="3pvexouk9jir3" />
                  <p className={`text-sm font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1782" data-mp="2td0kn8407hjw">
                    No calls found
                  </p>
                </div> : filteredCalls.map((call, idx) => <motion.button key={call.id} initial={call.id === 999 ? {
            opacity: 0,
            y: -20
          } : false} animate={call.id === 999 ? {
            opacity: 1,
            y: 0
          } : undefined} transition={call.id === 999 ? {
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94]
          } : undefined} style={call.id === 999 ? {
            willChange: 'transform, opacity'
          } : undefined} data-tour={idx === 0 ? 'calls-list-item' : undefined} onClick={() => handleSelectCall(call)} className={`relative w-full px-5 py-3.5 flex items-center gap-3.5 transition-colors ${call.isEmergency ? isLight ? 'bg-red-50/70 hover:bg-red-50 active:bg-red-100/70' : 'bg-red-500/[0.07] hover:bg-red-500/[0.11] active:bg-red-500/[0.14]' : isLight ? 'hover:bg-white/80 active:bg-white' : 'hover:bg-white/[0.02] active:bg-white/[0.04]'} ${idx < filteredCalls.length - 1 ? isLight ? 'border-b border-slate-100/80' : 'border-b border-white/[0.04]' : ''}`} data-id="element-1783" data-mp="1lu9sitq8kgj3" data-mpi={idx}>
                    {call.isEmergency && <span aria-hidden="true" className="absolute left-0 top-0 h-full w-[3px] bg-red-500" data-id="element-1784" data-mp="2ac0gdp6xvrr0" />}
                    <div className="relative" data-id="element-1785" data-mp="36cc0yjj8iwwv">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] flex-shrink-0 ${call.color}`} data-id="element-1786" data-mp="2roqviiyw0lls">
                        {call.initials}
                      </div>
                      {call.isLive && <div className="absolute -top-0.5 -right-0.5" data-id="element-1787" data-mp="2mpecyaf1u0p0">
                          <span className={`inline-flex rounded-full h-3 w-3 border-2 ${call.isEmergency ? 'bg-red-500' : 'bg-vexu'} ${isLight ? 'border-slate-50' : 'border-[#0A0A0A]'}`} data-id="element-1788" data-mp="2mnjpaoce7uh8" />
                        </div>}
                    </div>
                    <div className="flex-1 text-left min-w-0" data-id="element-1789" data-mp="bxla0rf3xdvh">
                      <div className="flex items-center gap-2" data-id="element-1790" data-mp="bwtm82rr4kfr">
                        <span className={`text-[14px] font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1791" data-mp="1weuapuh9bf5f">
                          {call.name}
                        </span>
                        {call.isEmergency && <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-[5px] flex-shrink-0 ${isLight ? 'bg-red-600 text-white' : 'bg-red-600/90 text-white'}`} data-id="element-1792" data-mp="21egp478tn1ft">
                            <Siren className="h-2.5 w-2.5" data-id="element-1793" data-mp="2p4ujw5isjee3" />
                            Emergency
                          </span>}
                        {call.isLive && !call.isEmergency && <span className={`text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-[5px] flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20' : 'bg-vexu/10 text-vexu border border-vexu/20'}`} data-id="element-1794" data-mp="urbfqcu98xjt">
                            Live
                          </span>}
                        {call.isFirstCall && <span className={`text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-[5px] flex-shrink-0 ${isLight ? 'bg-slate-100 text-slate-500 border border-slate-200' : 'bg-white/5 text-zinc-400 border border-white/10'}`} data-id="element-1795" data-mp="1oidjphom749g">
                            First call
                          </span>}
                      </div>
                      <div className={`flex items-center gap-1.5 text-[12px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1796" data-mp="2c5v42s5jv02e">
                        {call.isLive ? <>
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${call.isEmergency ? 'bg-red-500' : 'bg-vexu'}`} data-id="element-1797" data-mp="2m1omgs5y9ns3" />
                            <span className={`font-medium ${call.isEmergency ? isLight ? 'text-red-600' : 'text-red-400' : isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1798" data-mp="5jfmkuv162ij">
                              {call.isEmergency ? `Urgent · ${call.duration}` : call.duration}
                            </span>
                          </> : <>
                            {call.type === 'incoming' && <PhoneIncoming className={`w-3 h-3 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1799" data-mp="eh9i43kz0s4n" />}
                            {call.type === 'outgoing' && <PhoneOutgoing className={`w-3 h-3 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1800" data-mp="rnni8jux2sea" />}
                            <span data-id="element-1801" data-mp="hs2a03z98vw3">{call.group}</span>
                            <span className={`${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1802" data-mp="mpc6g3l2a7tf">
                              ·
                            </span>
                            <span data-id="element-1803" data-mp="1fd81z4yw9tom">{call.duration}</span>
                          </>}
                      </div>
                    </div>
                    <div className={`text-[11px] font-medium flex-shrink-0 ${call.isEmergency ? isLight ? 'text-red-600' : 'text-red-400' : call.isLive ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1804" data-mp="15ogfvdgim3lm">
                      {call.time}
                    </div>
                  </motion.button>)}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Caller History Overview */}
      <AnimatePresence data-id="element-1805" data-mp="3sl91ld9huxpl">
        {selectedCaller && callerProfile && !selectedCall && <motion.div key="caller-history" initial={{
        opacity: 0,
        x: 32
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 32
      }} transition={{
        duration: 0.24
      }} className={`absolute inset-0 z-20 flex flex-col ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-1806" data-mp="3nlcx7cwvx49i">
            <header className={`px-5 pt-6 pb-4 border-b ${isLight ? 'bg-white border-slate-200' : 'bg-[#141414] border-white/5'}`} data-id="element-1807" data-mp="o6hpdtnxsohd">
              <div className="flex items-center gap-3" data-id="element-1808" data-mp="k78swh1iici4">
                <button onClick={handleCallerBack} aria-label="Back to Calls" className={`p-1.5 -ml-1.5 rounded-full transition-colors ${isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/5'}`} data-id="element-1809" data-mp="3d0fv3oslv1mf">
                  <ArrowLeft className="w-5 h-5" data-id="element-1810" data-mp="3qzp7sehlrdln" />
                </button>
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold ${callerProfile.color}`} data-id="element-1811" data-mp="2u9512yrl28qb">
                  {callerProfile.initials}
                </div>
                <div className="min-w-0 flex-1" data-id="element-1812" data-mp="338s6dvnfnyon">
                  <h1 className={`text-[17px] font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1813" data-mp="33808ge6d5ov7">
                    {callerProfile.name}
                  </h1>
                  <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1814" data-mp="2fk0l7o89ggld">
                    {getCallPhoneNumber(callerProfile)} · {callerProfile.group}
                  </p>
                </div>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-5 pb-10" data-id="element-1815" data-mp="1yzt5v75ra0b3">
              <section className={`rounded-2xl p-4 mb-6 ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-1816" data-mp="1z25t9zua92vj">
                <div className="flex items-start justify-between gap-4" data-id="element-1817" data-mp="1p36m6lp61fan">
                  <div data-id="element-1818" data-mp="1kadh1wpsc4y1">
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1819" data-mp="1k9u6j12gsiu1">
                      Previous calls
                    </p>
                    <div className="flex items-baseline gap-1.5 mt-1" data-id="element-1820" data-mp="16bxb47zqyigy">
                      <span className={`text-3xl font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1821" data-mp="16d8dc3djni5y">
                        {callerHistoryCalls.length}
                      </span>
                      <span className={`text-sm font-medium ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1822" data-mp="2u70sywpxt7dw">
                        {callerHistoryCalls.length === 1 ? 'call' : 'calls'}
                      </span>
                    </div>
                    <p className={`text-[12px] mt-1.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1823" data-mp="26i93we7n955c">
                      {callerHistoryCalls.length === 0 ? 'This is their first call' : `${callerHistoryCalls.length} earlier ${callerHistoryCalls.length === 1 ? 'conversation' : 'conversations'}`}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/10 text-vexu'}`} data-id="element-1824" data-mp="1nkeysb9mzds8">
                    <Phone className="w-5 h-5" data-id="element-1825" data-mp="18x6jozd7ioaz" />
                  </div>
                </div>
                <div className={`mt-4 pt-3 border-t flex items-center gap-2 text-[11px] ${isLight ? 'border-slate-100 text-slate-500' : 'border-white/5 text-zinc-400'}`} data-id="element-1826" data-mp="13zel5mi243xt">
                  <Clock3 className="w-3.5 h-3.5 flex-shrink-0" data-id="element-1827" data-mp="1dupyc0dth33g" />
                  {callerHistoryCalls.length > 0 ? `Most recent prior call: ${callerHistoryCalls[0]?.time}` : `Current call: ${callerOverviewCall?.time}`}
                </div>
              </section>

              <div className="flex items-center justify-between mb-2.5" data-id="element-1828" data-mp="21nok1smjqbkp">
                <h2 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1829" data-mp="1wntxg19t085g">
                  Previous calls
                </h2>
                <span className={`text-[11px] font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1830" data-mp="26lq3ienvwcwp">
                  Newest first
                </span>
              </div>

              {callerHistoryCalls.length > 0 ? <>
                  <div className={`rounded-2xl overflow-hidden ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-1831" data-mp="2y0fsaetsg63g">
                    {callerHistoryCalls.map((call, index) => {
                const DirectionIcon = call.type === 'outgoing' ? PhoneOutgoing : PhoneIncoming;
                return <button key={call.id} onClick={() => openCallDetail(call)} aria-label={`Open call from ${call.time}, ${call.duration}. Summary: ${call.summary}`} className={`w-full text-left px-4 py-4 transition-colors ${isLight ? 'hover:bg-slate-50 active:bg-slate-100' : 'hover:bg-white/[0.03] active:bg-white/[0.06]'} ${index < callerHistoryCalls.length - 1 ? isLight ? 'border-b border-slate-100' : 'border-b border-white/5' : ''}`} data-id="element-1832" data-mp="2fb96uk1m10dy" data-mpi={index}>
                          <div className="flex items-start gap-3" data-id="element-1833" data-mp="puni4swurlzu">
                            <div className={`w-8 h-8 mt-0.5 rounded-full flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/5 text-zinc-400'}`} data-id="element-1834" data-mp="usf8hihun01x">
                              <DirectionIcon className="w-3.5 h-3.5" data-id="element-1835" data-mp="24jg2pzbbuay" />
                            </div>
                            <div className="min-w-0 flex-1" data-id="element-1836" data-mp="71rz0ssac2d9">
                              <div className="flex items-center justify-between gap-2" data-id="element-1837" data-mp="bu2v1kjwn40j">
                                <p className={`text-[13px] font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1838" data-mp="3akcvf1hwqzqf">
                                  {call.time}
                                </p>
                                <span className={`text-[11px] flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1839" data-mp="mm34l7bkgw26">
                                  {call.duration}
                                </span>
                              </div>
                              <div className={`flex items-center gap-1.5 mt-1 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1840" data-mp="3pa68wnjl50z">
                                <Phone className="w-3 h-3 flex-shrink-0" data-id="element-1841" data-mp="cq019y4zardn" />
                                <span className="text-[11px] font-medium" data-id="element-1842" data-mp="co5esfe26r7g">
                                  {getCallPhoneNumber(call)}
                                </span>
                              </div>
                              <p className={`text-[12px] leading-relaxed mt-1 pr-2 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1843" data-mp="1o78269gf1pd2">
                                {call.summary}
                              </p>
                            </div>
                            <ChevronRight className={`w-4 h-4 mt-1 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1844" data-mp="1hpeq9s5r4hs9" />
                          </div>
                        </button>;
              })}
                  </div>
                  <div className={`flex items-center gap-2 mt-4 px-1 text-[11px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1845" data-mp="2fd6o8ocmvn0w">
                    <FileText className="w-3.5 h-3.5" data-id="element-1846" data-mp="25n3wssl5uh4o" />
                    Tap any call to view its full transcript and summary.
                  </div>
                </> : <div className={`rounded-2xl p-5 text-center ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-1847" data-mp="2tav93w6pau5y">
                  <div className={`w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center ${isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-zinc-500'}`} data-id="element-1848" data-mp="19f910d2yzojz">
                    <FileText className="w-5 h-5" data-id="element-1849" data-mp="vi2huhec3nu8" />
                  </div>
                  <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1850" data-mp="vjx2rvplvfyb">
                    No previous calls
                  </p>
                  <p className={`text-[12px] leading-relaxed mt-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1851" data-mp="1o8uc39bua2ex">
                    This is {callerProfile.name}'s first call with you.
                  </p>
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Call Detail View */}
      <AnimatePresence data-id="element-1852" data-mp="2739aoxu8nt28">
        {selectedCall && <motion.div key="detail" initial={{
        opacity: 0,
        x: 40
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 40
      }} transition={{
        duration: 0.25
      }} className={`absolute inset-0 z-20 flex flex-col ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-1853" data-mp="2eha9n53wrkx6">
            {/* WhatsApp Header */}
            <div className={`px-4 pt-6 pb-3 flex items-center gap-3 ${isLight ? 'bg-white border-b border-slate-200' : 'bg-[#141414] border-b border-white/5'}`} data-id="element-1854" data-mp="32cv1csjvr0uj">
              <button onClick={() => handleBack()} className={`p-1 rounded-full ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-1855" data-mp="3c2xtkqj6gart">
                <ArrowLeft className="w-5 h-5" data-id="element-1856" data-mp="3l45rh7hs79ld" />
              </button>
              {!selectedCall.isLive ? <button type="button" onClick={() => {
            setSelectedCaller(selectedCall.name);
            setCallerOverviewCall(selectedCall);
            setSelectedCall(null);
          }} aria-label={`View ${selectedCall.name}'s call history`} className={`flex items-center gap-3 flex-1 min-w-0 -my-1.5 -ml-1.5 p-1.5 rounded-xl text-left transition-colors ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'}`} data-id="element-1857" data-mp="1m18msb3bnmtq">
                  <div className="relative" data-id="element-1858" data-mp="1zty96gmritrd">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${selectedCall.color}`} data-id="element-1859" data-mp="2dqbs7u6y337y">
                      <span className="text-xs font-bold" data-id="element-1860" data-mp="jd2cvlrpwnhs">
                        {selectedCall.initials}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0" data-id="element-1861" data-mp="57sd0a3x2bqd">
                    <div className="flex items-center gap-1.5" data-id="element-1862" data-mp="58v3igxbuc5r">
                      <h2 className={`text-sm font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1863" data-mp="995kspqkkozx">
                        {selectedCall.name}
                      </h2>
                      <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1864" data-mp="g1yrt1uz3to" />
                    </div>
                    <p className={`text-[11px] ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1865" data-mp="3mpcwvn5bfhy4">
                      {selectedCall.group} · {selectedCall.time}
                    </p>
                  </div>
                </button> : <>
                  <div className="relative" data-id="element-1866" data-mp="16xp3g1pig7k2">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${selectedCall.color}`} data-id="element-1867" data-mp="1bw8swc3bc1h6">
                      <span className="text-xs font-bold" data-id="element-1868" data-mp="2kisc0h9jkn1l">
                        {selectedCall.initials}
                      </span>
                    </div>
                    {selectedCall.isLive && <div className="absolute -top-0.5 -right-0.5" data-id="element-1869" data-mp="2aoiaoa2mgeeq">
                        <span className={`inline-flex rounded-full h-2.5 w-2.5 border-2 border-white dark:border-[#141414] ${selectedCall.isEmergency ? 'bg-red-500' : 'bg-vexu'}`} data-id="element-1870" data-mp="26orm8d0kiadd" />
                      </div>}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1871" data-mp="1rsimzbgofx0y">
                    <div className="flex items-center gap-2" data-id="element-1872" data-mp="1nqx51bpacsjw">
                      <h2 className={`text-sm font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1873" data-mp="1nsi33bat6tmi">
                        {selectedCall.name}
                      </h2>
                      {selectedCall.isEmergency ? <span className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white" data-id="element-1874" data-mp="13wf31w30k33t">
                          <Siren className="h-2.5 w-2.5" data-id="element-1875" data-mp="1dnjgusgydhx2" />
                          Emergency
                        </span> : selectedCall.isLive && <span className={`text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-md ${isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20' : 'bg-vexu/10 text-vexu border border-vexu/20'}`} data-id="element-1876" data-mp="kyunjfm52gvv">
                            Live
                          </span>}
                    </div>
                    <p className={`text-[11px] ${selectedCall.isEmergency ? isLight ? 'font-semibold text-red-600' : 'font-semibold text-red-400' : selectedCall.isLive ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1877" data-mp="25d4u85glp0z1">
                      {selectedCall.isLive ? `Live · ${selectedCall.duration}` : `${selectedCall.group} · ${selectedCall.time}`}
                    </p>
                  </div>
                </>}
              <div className="flex items-center gap-1" data-id="element-1878" data-mp="1sve7mshlj8qs">
                <button onClick={() => setShowCallConfirm(true)} className={`p-2 rounded-full ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/5'}`} data-id="element-1879" data-mp="1swz08ckcohm0">
                  <Phone className="w-4 h-4" data-id="element-1880" data-mp="27seqe6ht7td4" />
                </button>
                {!selectedCall.isLive && <button onClick={() => setShowDeleteConfirm(true)} className={`p-2 rounded-full ${isLight ? 'text-red-500 hover:bg-red-50' : 'text-red-400 hover:bg-red-500/10'}`} data-id="element-1881" data-mp="3e6w56xp0dr6q">
                    <Trash2 className="w-4 h-4" data-id="element-1882" data-mp="17lrrv1buk3ei" />
                  </button>}
              </div>
            </div>

            {/* Live Call Banner */}
            {selectedCall.isLive && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} className={`px-4 py-2.5 flex items-center justify-between ${selectedCall.isEmergency ? isLight ? 'bg-red-50 border-b border-red-200' : 'bg-red-500/[0.09] border-b border-red-500/20' : isLight ? 'bg-vexu/5 border-b border-vexu/10' : 'bg-vexu/[0.03] border-b border-vexu/10'}`} data-id="element-1883" data-mp="xrizqeqqacgv">
                <div className="flex items-center gap-2" data-id="element-1884" data-mp="297pr4q0ssw0o">
                  <div className="flex items-center gap-1.5" data-id="element-1885" data-mp="24a7fgrvec2oz">
                    {selectedCall.isEmergency ? <motion.span animate={{
                opacity: [1, 0.3, 1]
              }} transition={{
                duration: 1.1,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="flex items-center gap-1.5" data-id="element-1886" data-mp="2k8kxlgcbdgm">
                        <Siren className={`h-3 w-3 ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-1887" data-mp="lb9uf3f36ref" />
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-1888" data-mp="q91n3znk2hjo">
                          Emergency Call
                        </span>
                      </motion.span> : <>
                        <span className="w-1.5 h-1.5 rounded-full bg-vexu" data-id="element-1889" data-mp="312e8nfwmlri5" />
                        <span className={`text-[10px] font-semibold uppercase tracking-widest ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1890" data-mp="310b65weae5wj">
                          Active Call
                        </span>
                      </>}
                  </div>
                  <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1891" data-mp="1o459paz7si8g">
                    ·
                  </span>
                  <span className={`text-[10px] ${selectedCall.isEmergency ? isLight ? 'font-medium text-red-600/80' : 'font-medium text-red-400/80' : isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1892" data-mp="10bx3cozv5pir">
                    {selectedCall.isEmergency ? 'Escalating to you now' : 'AI Agent handling'}
                  </span>
                </div>
                <div className="flex items-center gap-0.5" data-id="element-1893" data-mp="cp8l226xzv1m">
                  {[...Array(4)].map((_, i) => <motion.div key={i} animate={{
              height: ['3px', '10px', '5px', '8px', '3px']
            }} transition={{
              duration: selectedCall.isEmergency ? 0.9 : 1.2,
              repeat: Infinity,
              delay: i * 0.15
            }} className={`w-[2px] rounded-full ${selectedCall.isEmergency ? 'bg-red-500/70' : 'bg-vexu/60'}`} data-id="element-1894" data-mp="pepon10edvua" data-mpi={i} />)}
                </div>
              </motion.div>}

            {/* Scrollable Content */}
            <div ref={detailScrollRef} className={`relative flex-1 overflow-y-auto transition-colors ${showCommandComposer ? 'pb-28' : 'pb-8'} ${selectedCall.isEmergency ? isLight ? 'bg-red-50/40' : 'bg-red-500/[0.04]' : ''}`} data-id="element-1895" data-mp="1n1olpcv055sp">

              {/* Date pill */}
              <div className="flex justify-center py-3" data-id="element-1896" data-mp="3h9abmlfwvof4">
                <span className={`text-[10px] font-medium px-3 py-1 rounded-full ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/5 text-zinc-500'}`} data-id="element-1897" data-mp="3hb3mye1gywtg">
                  {selectedCall.isLive ? 'Now' : selectedCall.time === 'Yesterday' || selectedCall.time === 'Mon' || selectedCall.time === 'Sun' ? selectedCall.time : 'Today'}
                </span>
              </div>

              {/* Chat Thread */}
              <div className="px-5 space-y-1.5 pb-4" data-id="element-1898" data-mp="3q978hacgftl9">
                {selectedCall.messages.map((msg, idx) => {
              const isCaller = msg.sender === 'user';
              // Only show the speaker's avatar + name on the first message
              // of a consecutive run, keeping the thread clean.
              const isRunStart = idx === 0 || selectedCall.messages[idx - 1].sender !== msg.sender || selectedCall.messages[idx - 1].internal !== msg.internal;
              // Private owner↔AI side-channel: rendered as a centered
              // "internal note", clearly separate from the spoken call.
              // For COMPLETED calls these are relocated to below the call
              // summary (rendered separately), since the commands happen
              // after the call ended. During a LIVE call they stay inline.
              if (msg.internal) {
                if (!selectedCall.isLive) return null;
                const runStartInternal = idx === 0 || !selectedCall.messages[idx - 1].internal;
                return <div key={idx} data-id="element-1899" data-mp="3vesrhxv4tlly">
                        {runStartInternal && <div className="flex items-center gap-2 w-full pt-3 pb-1.5" data-id="element-1900" data-mp="49avmy9fn2an">
                            <span className={`flex-1 h-px ${isLight ? 'bg-amber-200/70' : 'bg-amber-400/15'}`} data-id="element-1901" data-mp="3gsdidkpu7hls" />
                            <span className={`flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-amber-700/80' : 'text-amber-300/70'}`} data-id="element-1902" data-mp="3csv1r151tsor">
                              <StickyNote className="w-2.5 h-2.5" data-id="element-1903" data-mp="3lbu0e9tqsto" />
                              Private — not heard by caller
                            </span>
                            <span className={`flex-1 h-px ${isLight ? 'bg-amber-200/70' : 'bg-amber-400/15'}`} data-id="element-1904" data-mp="3vnjsais200ke" />
                          </div>}
                        {renderInternalBubble(msg, idx)}
                      </div>;
              }
              return <motion.div key={idx} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.05,
                duration: 0.2
              }} className={`flex items-end gap-2 ${isCaller ? 'flex-row-reverse' : 'flex-row'} ${isRunStart ? 'pt-2.5' : ''}`} data-id="element-1905" data-mp="icb2lpanthd9" data-mpi={idx}>
                      {/* Speaker avatar — shown once per run, spacer otherwise */}
                      <div className="w-7 flex-shrink-0 self-end" data-id="element-1906" data-mp="2q9wws5e0bxyz">
                        {isRunStart && (isCaller ? <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${selectedCall.color}`} data-id="element-1907" data-mp="1xn2d3ap0l2mk">
                              {selectedCall.initials}
                            </div> : <div className="w-7 h-7 rounded-full bg-vexu flex items-center justify-center" data-id="element-1908" data-mp="fqijw3dsjrfr">
                              <Bot className="w-4 h-4 text-white" data-id="element-1909" data-mp="aojeyp2ekmgp" />
                            </div>)}
                      </div>

                      <div className={`flex flex-col max-w-[68%] ${isCaller ? 'items-end' : 'items-start'}`} data-id="element-1910" data-mp="rojyg5c9476">
                        {/* Speaker name label */}
                        {isRunStart && <span className={`text-[10px] font-semibold mb-1 px-0.5 ${isCaller ? isLight ? 'text-slate-500' : 'text-zinc-400' : isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1911" data-mp="3o5isvf1ca9dm">
                            {isCaller ? selectedCall.name : 'Vexu AI'}
                          </span>}
                        <div className={`rounded-[14px] px-3 py-2 ${isCaller ? 'bg-vexu text-white rounded-tr-[4px]' : isLight ? 'bg-white text-slate-800 rounded-tl-[4px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-slate-100/80' : 'bg-[#1A1A1A] text-zinc-100 rounded-tl-[4px] border border-white/[0.04]'}`} data-id="element-1912" data-mp="2j2diqe2ckggx">
                          <div className="flex items-center gap-2" data-id="element-1913" data-mp="1zffklc4g63vc">
                            <button onClick={() => togglePlay(idx)} className={`flex-shrink-0 w-5.5 h-5.5 w-[22px] h-[22px] rounded-full flex items-center justify-center transition-colors ${playingIdx === idx ? msg.sender === 'user' ? 'bg-white/20 text-white' : 'bg-vexu text-white' : msg.sender === 'user' ? 'bg-white/10 text-white/70' : isLight ? 'bg-slate-50 text-slate-400 border border-slate-100' : 'bg-white/[0.06] text-zinc-500'}`} data-id="element-1914" data-mp="1zfpcx6cysn47">
                              {playingIdx === idx ? <div className="w-2 h-2 bg-current rounded-[2px]" data-id="element-1915" data-mp="3nbn9vmct1vds" /> : <Play className="w-2.5 h-2.5 ml-[1px]" data-id="element-1916" data-mp="3s6195zul8mnq" />}
                            </button>
                            <div className="flex-1 min-w-0 relative" data-id="element-1917" data-mp="3jatp31unswp4">
                              {/* Text always rendered to maintain bubble size */}
                              <motion.p animate={{
                          opacity: playingIdx === idx ? 0 : 1
                        }} transition={{
                          duration: 0.2
                        }} className="text-[12px] leading-[1.45] tracking-[-0.01em]" data-id="element-1918" data-mp="atp3mjv5c7fd">
                                {msg.text}
                              </motion.p>
                              {/* Waveform overlay */}
                              <AnimatePresence data-id="element-1919" data-mp="26kbi1784vjtp">
                                {playingIdx === idx && <motion.div initial={{
                            opacity: 0
                          }} animate={{
                            opacity: 1
                          }} exit={{
                            opacity: 0
                          }} transition={{
                            duration: 0.2
                          }} className="absolute inset-0 flex items-center gap-[2px]" data-id="element-1920" data-mp="22k18xlp1odam">
                                    {[...Array(28)].map((_, i) => <motion.div key={i} animate={{
                              height: ['15%', '100%', '35%', '75%', '15%']
                            }} transition={{
                              duration: 0.5 + Math.random() * 0.5,
                              repeat: Infinity
                            }} className={`w-[2px] rounded-full ${msg.sender === 'user' ? 'bg-white/35' : 'bg-vexu/40'}`} data-id="element-1921" data-mp="3494tsfnf1pnr" data-mpi={i} />)}
                                  </motion.div>}
                              </AnimatePresence>
                            </div>
                          </div>
                          <div className={`flex items-center justify-end gap-1 mt-0.5 ${isCaller ? 'text-white/40' : isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1922" data-mp="fntzhppdlmm5">
                            <span className="text-[9px] tracking-wide" data-id="element-1923" data-mp="pjetp1o7dilc">
                              {msg.time}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>;
            })}

                {/* Live typing indicator */}
                {selectedCall.isLive && liveTyping && <motion.div initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.15
            }} className="flex items-end gap-2" data-id="element-1924" data-mp="18danmb5yqhck">
                    <div className="w-7 flex-shrink-0 self-end" data-id="element-1925" data-mp="1rzcacyv003m2">
                      <div className="w-7 h-7 rounded-full bg-vexu flex items-center justify-center" data-id="element-1926" data-mp="1wuj69ki1040f">
                        <Bot className="w-4 h-4 text-white" data-id="element-1927" data-mp="1i5vdtn3h8aep" />
                      </div>
                    </div>
                    <div className={`rounded-[14px] rounded-tl-[4px] px-3.5 py-2.5 ${isLight ? 'bg-white border border-slate-100/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]' : 'bg-[#1A1A1A] border border-white/[0.04]'}`} data-id="element-1928" data-mp="1mzzn4d07suiy">
                      <div className="flex gap-[3px] items-center" data-id="element-1929" data-mp="z9deokmat88f">
                        {[0, 0.15, 0.3].map((delay, i) => <motion.div key={i} animate={{
                    opacity: [0.3, 0.8, 0.3]
                  }} transition={{
                    duration: 0.9,
                    repeat: Infinity,
                    delay
                  }} className={`w-[5px] h-[5px] rounded-full ${isLight ? 'bg-slate-300' : 'bg-zinc-600'}`} data-id="element-1930" data-mp="146nf0vb874b1" data-mpi={i} />)}
                      </div>
                    </div>
                  </motion.div>}
              </div>

              {/* AI Summary Card - only for completed calls */}
              {!selectedCall.isLive && <div className="px-4" data-id="element-1931" data-mp="3avfgtcez6ob4">
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: selectedCall.messages.length * 0.07 + 0.2
            }} className={`overflow-hidden rounded-2xl ${selectedCall.isEmergency ? isLight ? 'bg-white border border-red-200 shadow-sm shadow-red-900/5' : 'bg-[#171112] border border-red-500/25' : isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-1932" data-mp="24g4uhi0ztwyb">
                    {selectedCall.isEmergency && <div className={`flex items-center gap-2 px-4 py-2 ${isLight ? 'bg-red-600' : 'bg-red-600/90'}`} data-id="element-1933" data-mp="2d492frocvurg">
                        <Siren className="h-3.5 w-3.5 flex-shrink-0 text-white" data-id="element-1934" data-mp="2mz100vhxuhm8" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-white" data-id="element-1935" data-mp="no9qy3x9lt8n">
                          Emergency call
                        </span>
                      </div>}
                    <div className="p-5" data-id="element-1936" data-mp="2uiadp0xh92h3">
                    <div className="flex items-center gap-2 mb-3" data-id="element-1937" data-mp="2pks4d9fjsl8k">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${selectedCall.isEmergency ? isLight ? 'bg-red-100' : 'bg-red-500/15' : 'bg-vexu/15'}`} data-id="element-1938" data-mp="2pkibne34bzz6">
                        {selectedCall.isEmergency ? <Siren className={`w-3.5 h-3.5 ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-1939" data-mp="2bliv6tprere3" /> : <CheckCircle2 className="w-3.5 h-3.5 text-vexu" data-id="element-1940" data-mp="2kmgr0oq1nsll" />}
                      </div>
                      <h3 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1941" data-mp="2gm6ndipq5e9w">
                        Call Summary
                      </h3>
                    </div>
                    <div className={`flex items-center gap-4 mb-3 pb-3 border-b ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-1942" data-mp="3cujzaloj2ht">
                      <div data-id="element-1943" data-mp="3pkltmqtbtz9e">
                        <p className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1944" data-mp="3pldqe4ynu319">
                          Duration
                        </p>
                        <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1945" data-mp="m67w7yhm4k83">
                          {selectedCall.duration}
                        </p>
                      </div>
                      <div data-id="element-1946" data-mp="hbu88h5a5fdg">
                        <p className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1947" data-mp="hb2d17flhjnp">
                          Group
                        </p>
                        <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1948" data-mp="35icvw1f7evos">
                          {selectedCall.group}
                        </p>
                      </div>
                    </div>
                    <p className={`text-xs leading-relaxed ${selectedCall.booking ? 'mb-3' : 'mb-2'} ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-1949" data-mp="31grmwr5sj1jv">
                      {selectedCall.summary}
                    </p>
                    {selectedCall.booking && <button type="button" onClick={() => onViewBookedEvent?.(selectedCall.booking!.eventId)} aria-label={`Open booked meeting: ${selectedCall.booking.title}`} className={`mb-3 w-full rounded-xl border px-2.5 py-2 text-left transition-all active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'border-vexu/20 bg-vexu/[0.05] hover:bg-vexu/[0.09]' : 'border-vexu/20 bg-vexu/[0.09] hover:bg-vexu/[0.14]'}`} data-id="element-1950" data-mp="3f97jpa83m3cc">
                        <span className="flex items-center gap-2.5" data-id="element-1951" data-mp="2aa16t5vmje7v">
                          <span className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg ${isLight ? 'bg-white text-vexu-700 shadow-[0_1px_2px_rgba(15,23,42,0.06)]' : 'bg-vexu/15 text-vexu'}`} data-id="element-1952" data-mp="2a758vhjs0pjf"><CalendarDays className="h-3.5 w-3.5" data-id="element-1953" data-mp="1hl3oj2rexhf4" /></span>
                          <span className="min-w-0 flex-1" data-id="element-1954" data-mp="1dka5amox0qsk">
                            <span className={`block truncate text-[13px] font-semibold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1955" data-mp="1radi6n1wn85c">{selectedCall.booking.title}</span>
                            <span className={`mt-0.5 block truncate text-[10.5px] leading-tight ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1956" data-mp="1mi3uzes6cm6r"><span className={`font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1957" data-mp="tvj56ircki5b">Booked</span> · {selectedCall.booking.time} · Connected calendar</span>
                          </span>
                          <ChevronRight className={`h-3.5 w-3.5 flex-shrink-0 ${isLight ? 'text-vexu-700/70' : 'text-vexu/70'}`} data-id="element-1958" data-mp="2yw2camjkhvci" />
                        </span>
                      </button>}
                    <button type="button" onClick={() => {
                  setSelectedCaller(selectedCall.name);
                  setCallerOverviewCall(selectedCall);
                  setSelectedCall(null);
                }} className={`w-full flex items-center justify-between gap-3 mb-3 px-3 py-2.5 rounded-xl text-left transition-colors ${isLight ? 'bg-vexu/5 border border-vexu/15 hover:bg-vexu/10' : 'bg-vexu/[0.07] border border-vexu/15 hover:bg-vexu/10'}`} aria-label={`View ${selectedCall.name}'s call history`} data-id="element-1959" data-mp="38monm4k93zpe">
                      <span className={`text-[11px] leading-snug ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1960" data-mp="4a1jzg7jfdns">
                        <span className="font-semibold" data-id="element-1961" data-mp="1kpxj4ldmnxwm">
                          {selectedCallHistoryCount > 0 ? `${selectedCall.name} has ${selectedCallHistoryCount} previous ${selectedCallHistoryCount === 1 ? 'call' : 'calls'}` : `This is ${selectedCall.name}'s first call`}
                        </span>{' '}
                        in your call history.
                      </span>
                      <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1962" data-mp="wz1x64vurwxa" />
                    </button>
                    {/* Play Full Call Button */}
                    <button onClick={() => setPlayingIdx(playingIdx === -1 ? null : -1)} className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all ${playingIdx === -1 ? isLight ? 'bg-vexu/10 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20' : isLight ? 'bg-slate-50 border border-slate-100 hover:bg-slate-100/80' : 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]'}`} data-id="element-1963" data-mp="2rc1jshynhre2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${playingIdx === -1 ? 'bg-vexu text-white' : isLight ? 'bg-white text-slate-500 shadow-sm border border-slate-100' : 'bg-white/10 text-zinc-400'}`} data-id="element-1964" data-mp="1f3li0ywfciz1">
                        {playingIdx === -1 ? <Pause className="w-3.5 h-3.5" data-id="element-1965" data-mp="veb3ho8f042i" /> : <Play className="w-3.5 h-3.5 ml-0.5" data-id="element-1966" data-mp="hervqwjimuqq" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1967" data-mp="mca6msh3fk1d">
                        {playingIdx === -1 ? <div className="flex items-center gap-[2px] h-5" data-id="element-1968" data-mp="2rjmw2doj1hx">
                            {[...Array(32)].map((_, i) => <motion.div key={i} animate={{
                        height: ['15%', '100%', '35%', '75%', '15%']
                      }} transition={{
                        duration: 0.5 + Math.random() * 0.5,
                        repeat: Infinity
                      }} className="w-[2px] rounded-full bg-vexu/50" data-id="element-1969" data-mp="cp7ieuj40d15" data-mpi={i} />)}
                          </div> : <span className={`text-[12px] font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1970" data-mp="14h6nq7i54s6g">
                            Play Full Call
                          </span>}
                      </div>
                      <span className={`text-[10px] font-medium ${playingIdx === -1 ? 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1971" data-mp="1chuv6mhg8k8d">
                        {selectedCall.duration}
                      </span>
                    </button>
                    </div>
                  </motion.div>

                  {/* Post-call commands to the AI — logically after the summary */}
                  {showCommandLog && selectedCall.messages.some(m => m.internal) && <div className="mt-4" data-id="element-1972" data-mp="255gvnlvdr8r0">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1973" data-mp="254flrkq3osav">
                        <span className={`flex-1 h-px ${isLight ? 'bg-amber-200/70' : 'bg-amber-400/15'}`} data-id="element-1974" data-mp="5t5zokujpb2o" />
                        <span className={`flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-amber-700/80' : 'text-amber-300/70'}`} data-id="element-1975" data-mp="atttfea5s60l">
                          <StickyNote className="w-2.5 h-2.5" data-id="element-1976" data-mp="2f9ff9im76bmw" />
                          Your commands · private
                        </span>
                        <span className={`flex-1 h-px ${isLight ? 'bg-amber-200/70' : 'bg-amber-400/15'}`} data-id="element-1977" data-mp="2ac5jzmc4n5r5" />
                      </div>
                      <div className="space-y-1.5" data-id="element-1978" data-mp="330b201bsqgna">
                        {selectedCall.messages.filter(m => m.internal).map((m, i) => renderInternalBubble(m, i))}
                      </div>
                    </div>}
                </div>}
            </div>

            {/* Floating Command Input Bar */}
            <div className={`absolute bottom-5 left-4 right-4 z-30 ${showCommandComposer ? '' : 'hidden'}`} data-id="element-1979" data-mp="1hkvkj89zf2me">
              <div className={`flex gap-2 p-2 rounded-full shadow-lg backdrop-blur-xl ${isLight ? 'bg-white/80 border border-slate-200/60 shadow-slate-200/50' : 'bg-[#141414]/80 border border-white/10 shadow-black/40'}`} data-id="element-1980" data-mp="3hq982jw9vd8o">
                <input type="text" value={commandInput} onChange={e => setCommandInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSendCommand()} placeholder="Send a command to agent..." className={`flex-1 rounded-full px-4 py-2 text-sm outline-none bg-transparent ${isLight ? 'text-slate-900 placeholder:text-slate-400' : 'text-white placeholder:text-zinc-500'}`} data-id="element-1981" data-mp="3coa6nfk3eqed" />
                <button onClick={handleSendCommand} disabled={!commandInput.trim()} className="w-9 h-9 rounded-full bg-vexu text-white flex items-center justify-center disabled:opacity-40 transition-all flex-shrink-0" data-id="element-1982" data-mp="n7cgg0yi195v">
                  <Send className="w-4 h-4" data-id="element-1983" data-mp="27p56v6wjj34e" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Call Confirmation Modal */}
      <AnimatePresence data-id="element-1984" data-mp="2hh146fqsyp7k">
        {showCallConfirm && selectedCall && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1985" data-mp="1tq5f3qdqe840">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCallConfirm(false)} data-id="element-1986" data-mp="1yruufi7zl7xw" />
            <motion.div initial={{
          y: 120,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 120,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 28
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1987" data-mp="3a3vmp7427eie">
              <div className="p-6 space-y-5" data-id="element-1988" data-mp="6e0p31kfeuqc">
                <div className="flex items-start justify-between" data-id="element-1989" data-mp="1m0jashod7lu">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-1990" data-mp="1k5x7auo39g6">
                    <Phone className={`w-6 h-6 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1991" data-mp="u4e6ly5dxb6o" />
                  </div>
                  <button onClick={() => setShowCallConfirm(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-1992" data-mp="xl8ybalykg61">
                    <X className="w-4 h-4" data-id="element-1993" data-mp="16k44wnswzs9v" />
                  </button>
                </div>

                <div data-id="element-1994" data-mp="1bm39u24ayx8x">
                  <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1995" data-mp="1bi5nz8qcbrlc">
                    Call {selectedCall.name}?
                  </h3>
                  <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1996" data-mp="1pbwwqew8z7g0">
                    This will open your phone dialer to call this contact
                    directly.
                  </p>
                </div>

                {/* Contact info card */}
                <div className={`flex items-center gap-3 p-3.5 rounded-xl ${isLight ? 'bg-slate-50 border border-slate-100' : 'bg-white/[0.03] border border-white/[0.06]'}`} data-id="element-1997" data-mp="24a8ukovcwm8r">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0 ${selectedCall.color}`} data-id="element-1998" data-mp="19n9z7sagl2rn">
                    {selectedCall.initials}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1999" data-mp="1xb9riwnf518c">
                    <p className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2000" data-mp="1x9yq33hg45ke">
                      {selectedCall.name}
                    </p>
                    <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2001" data-mp="277d4yynq0mal">
                      {selectedCall.group}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-1" data-id="element-2002" data-mp="7xwt1o1zm7f5">
                  <button onClick={() => setShowCallConfirm(false)} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors ${isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`} data-id="element-2003" data-mp="2jjymlrglmf68">
                    Cancel
                  </button>
                  <button onClick={() => {
                setShowCallConfirm(false);
                window.open('tel:+15550000000', '_self');
              }} className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors flex items-center justify-center gap-2" data-id="element-2004" data-mp="2erdz17oacyb5">
                    <Phone className="w-4 h-4" data-id="element-2005" data-mp="1vuw9mwwwtai2" />
                    Call Now
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Delete Call Confirmation Modal */}
      <AnimatePresence data-id="element-2006" data-mp="1q6e5dxp6n7pa">
        {showDeleteConfirm && selectedCall && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-2007" data-mp="1l71hd9mkrul4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} data-id="element-2008" data-mp="179cy2685ovi6" />
            <motion.div initial={{
          y: 120,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 120,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 28
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-2009" data-mp="xiibt1uwtvwq">
              <div className="p-6 space-y-5" data-id="element-2010" data-mp="353qgo9o98ktv">
                <div className="flex items-start justify-between" data-id="element-2011" data-mp="394k6x9p66bpx">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-red-50' : 'bg-red-500/10'}`} data-id="element-2012" data-mp="392puykvvltxz">
                    <Trash2 className={`w-6 h-6 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-2013" data-mp="2hefmjb1etoop" />
                  </div>
                  <button onClick={() => setShowDeleteConfirm(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-2014" data-mp="2q6hnrbjaz901">
                    <X className="w-4 h-4" data-id="element-2015" data-mp="1xnu6y8bcag4r" />
                  </button>
                </div>

                <div data-id="element-2016" data-mp="30yzxu74nu1fe">
                  <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2017" data-mp="30yhu6pfo4kxg">
                    Delete this call?
                  </h3>
                  <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2018" data-mp="3as6yjeoggt95">
                    The conversation with{' '}
                    <span className={`font-semibold ${isLight ? 'text-slate-800' : 'text-white'}`} data-id="element-2019" data-mp="2i3jka324j453">
                      {selectedCall.name}
                    </span>
                    , including the transcript and AI summary, will be
                    permanently removed. This action cannot be undone.
                  </p>
                </div>

                <div className="flex gap-3 pt-1" data-id="element-2020" data-mp="6e0gz6dk688x">
                  <button onClick={() => setShowDeleteConfirm(false)} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors ${isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`} data-id="element-2021" data-mp="29iyrbmuk309">
                    Cancel
                  </button>
                  <button onClick={handleDeleteCall} className="flex-1 py-3 rounded-xl font-medium text-sm bg-red-500 text-white hover:bg-red-600 transition-colors flex items-center justify-center gap-2" data-id="element-2022" data-mp="4uqoahq3rycs">
                    <Trash2 className="w-4 h-4" data-id="element-2023" data-mp="xiwdphs3qbiy" />
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Cloud Backup Prompt Overlay */}
      <AnimatePresence data-id="element-2024" data-mp="1bie421l1pwuf">
        {showBackupPrompt && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-2025" data-mp="1ga5k3i6ksk6b">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleBackupSkip} data-id="element-2026" data-mp="33l8d1536fu8p" />
            <motion.div initial={{
          y: 120,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 120,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 28
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-2027" data-mp="3vcoaju69v1dp">
              <AnimatePresence mode="wait" data-id="element-2028" data-mp="1vucfp9x9tfhc">
                {/* Step 1: Ask to enable backup */}
                {backupStep === 'ask' && <motion.div key="ask" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="p-6 space-y-5" data-id="element-2029" data-mp="20q9uvcwssfsj">
                    <div className="flex items-start justify-between" data-id="element-2030" data-mp="2tfqpmh7w48rx">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-2031" data-mp="u4pirfvxk8y3">
                        <CloudUpload className={`w-6 h-6 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2032" data-mp="3jw18uzeaeseb" />
                      </div>
                      <button onClick={handleBackupSkip} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-2033" data-mp="3ezj6252ip44m">
                        <X className="w-4 h-4" data-id="element-2034" data-mp="6opui5d8bwgi" />
                      </button>
                    </div>

                    <div data-id="element-2035" data-mp="1pvax17fu3vc">
                      <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2036" data-mp="1ok936xjz66v">
                        Back up your calls
                      </h3>
                      <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2037" data-mp="pfzfbasga7ne">
                        Keep your call recordings, transcripts, and AI summaries
                        safe in the cloud. You can restore them anytime.
                      </p>
                    </div>

                    <div className={`flex items-center gap-3 p-3 rounded-xl ${isLight ? 'bg-slate-50' : 'bg-white/[0.03]'}`} data-id="element-2038" data-mp="1919bx36nqoe0">
                      <Shield className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-2039" data-mp="z2tnx92xfv8q" />
                      <p className={`text-[11px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2040" data-mp="q36k6gmq6dci">
                        Your data is encrypted end-to-end and stored securely on
                        your cloud account.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-1" data-id="element-2041" data-mp="2enbhzzrjyjw">
                      <button onClick={handleBackupSkip} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors ${isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`} data-id="element-2042" data-mp="7cp4pdqwle93">
                        Not now
                      </button>
                      <button onClick={handleBackupEnable} className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-2043" data-mp="1zcb8ga7jxe2v">
                        Enable Backup
                      </button>
                    </div>
                  </motion.div>}

                {/* Step 2: Choose cloud provider */}
                {backupStep === 'provider' && <motion.div key="provider" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="p-6 space-y-4" data-id="element-2044" data-mp="2e0fo15bb59hf">
                    <div data-id="element-2045" data-mp="3s1ki37yvxxca">
                      <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2046" data-mp="3s23ru1edtff8">
                        Choose cloud account
                      </h3>
                      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2047" data-mp="asfed1t31jtm">
                        Where should we store your call backups?
                      </p>
                    </div>

                    <div className="space-y-2.5" data-id="element-2048" data-mp="2cld0kescte5l">
                      {/* Google Drive */}
                      <button onClick={() => handleBackupProviderSelect('google')} className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border transition-all active:scale-[0.98] ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`} data-id="element-2049" data-mp="2gm5azbcypp3g">
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-slate-100" data-id="element-2050" data-mp="1qblea3186fkx">
                          <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-2051" data-mp="xlwc3mcs49hq">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-2052" data-mp="xjjmqo4oywun" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-2053" data-mp="jt6fyp3jdo6w" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-2054" data-mp="2v9qgw54icm8q" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-2055" data-mp="2lh13h4pecyar" />
                          </svg>
                        </div>
                        <div className="flex-1 text-left" data-id="element-2056" data-mp="356ldgih80ckd">
                          <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2057" data-mp="3597kmh078q22">
                            Google Drive
                          </p>
                          <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2058" data-mp="3vhn273bvhxyo">
                            alex.founder@gmail.com
                          </p>
                        </div>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2059" data-mp="3hpyvjfke81p3" />
                      </button>

                      {/* iCloud */}
                      <button onClick={() => handleBackupProviderSelect('icloud')} className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border transition-all active:scale-[0.98] ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`} data-id="element-2060" data-mp="37r17seuskc75">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-b from-[#5AC8FA] to-[#007AFF] flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-2061" data-mp="x12psa64fhhp">
                          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2062" data-mp="2jw7i1dkv16bd">
                            <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" data-id="element-2063" data-mp="20x3cwfwfvwoz" />
                          </svg>
                        </div>
                        <div className="flex-1 text-left" data-id="element-2064" data-mp="24xvovgwp4foy">
                          <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2065" data-mp="29yjmwmjcdwso">
                            iCloud
                          </p>
                          <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2066" data-mp="3lbvo0pnpw1b5">
                            alex@icloud.com
                          </p>
                        </div>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2067" data-mp="35tghkwd2xnsq" />
                      </button>
                    </div>

                    <button onClick={handleBackupSkip} className={`w-full py-2.5 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-2068" data-mp="3fpt9bop2drku">
                      Skip for now
                    </button>
                  </motion.div>}

                {/* Step 3: Choose frequency */}
                {backupStep === 'frequency' && <motion.div key="frequency" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="p-6 space-y-4" data-id="element-2069" data-mp="1qdraq4tyjldh">
                    <div data-id="element-2070" data-mp="11jvacd7jiv2f">
                      <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2071" data-mp="11l6dqbx2q0t0">
                        Backup frequency
                      </h3>
                      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2072" data-mp="sl1929j9lgij">
                        How often should we back up your call data?
                      </p>
                    </div>

                    <div className="space-y-1.5" data-id="element-2073" data-mp="3opx38vxwtlan">
                      {[{
                  label: 'Daily',
                  desc: 'Every night at 2:00 AM',
                  value: 'daily'
                }, {
                  label: 'Weekly',
                  desc: 'Every Sunday at 2:00 AM',
                  value: 'weekly'
                }, {
                  label: 'Monthly',
                  desc: 'First day of each month',
                  value: 'monthly'
                }, {
                  label: 'Only when I tap "Back up"',
                  desc: 'Manual backups only',
                  value: 'manual'
                }].map(option => <button key={option.value} onClick={() => handleBackupFrequencySelect(option.value)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-colors ${isLight ? 'hover:bg-slate-50 active:bg-slate-100' : 'hover:bg-white/[0.03] active:bg-white/[0.06]'}`} data-id="element-2074" data-mp="2rylcn2nno74v">
                          <div data-id="element-2075" data-mp="1s0rxb29tpiwn">
                            <p className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2076" data-mp="1rzytoa65v9iq">
                              {option.label}
                            </p>
                            <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2077" data-mp="1e338xkgjrtmu">
                              {option.desc}
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isLight ? 'border-slate-200' : 'border-white/10'}`} data-id="element-2078" data-mp="44k26besax6a">
                            <div className="w-2 h-2 rounded-full" data-id="element-2079" data-mp="3r7ymkmdtebwb" />
                          </div>
                        </button>)}
                    </div>

                    <button onClick={handleBackupSkip} className={`w-full py-2.5 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-2080" data-mp="3hiyi4ngjmoaq">
                      Skip for now
                    </button>
                  </motion.div>}

                {/* Step 3: Done */}
                {backupStep === 'done' && <motion.div key="done" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="p-8 flex flex-col items-center text-center" data-id="element-2081" data-mp="33jfm2t10b3es">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
                delay: 0.1
              }} className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-2082" data-mp="32ueyvamrap1u">
                      <CheckCircle2 className={`w-8 h-8 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2083" data-mp="1eygw2e08imkc" />
                    </motion.div>
                    <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2084" data-mp="1opuzuxz7y8ja">
                      Backup enabled
                    </h3>
                    <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2085" data-mp="w1pficc2uljz">
                      {backupFrequency === 'daily' && `Backed up daily to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                      {backupFrequency === 'weekly' && `Backed up weekly to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                      {backupFrequency === 'monthly' && `Backed up monthly to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                      {backupFrequency === 'manual' && `Manual backup to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}